import {
  canonicalRecordJson,
  createMatchRecord,
  parseMatchRecord,
  summarizeMatchRecord,
  isMatchVersion,
  parseSavedMatch,
  sameMatchVersion,
  versionOf,
  type CommitResult,
  type LoadMatchResult,
  type MatchVersion,
  type MatchHistoryPort,
  type MatchRecordV1,
  type MatchWrite,
  type SavedMatchParseError,
  type SavedMatchV1,
  type StorageFailure,
  type StorageFailureCode,
  type StoragePort,
} from "../../features/match/persistence";

export const MATCH_DATABASE_NAME = "passo";
export const MATCH_DATABASE_VERSION = 2;
export const MATCH_OBJECT_STORE = "match";
export const MATCH_HISTORY_OBJECT_STORE = "match-history";
export const MATCH_HISTORY_COMPLETED_INDEX = "completedAt";
export const MATCH_SLOT_KEY = "current";
export const MATCH_COMMIT_WATCHDOG_MS = 1_000;
export const MATCH_SYNC_CHANNEL = "passo-match-sync-v1";

const SLOT_SCHEMA_VERSION = 2 as const;

type MatchSlot = Readonly<{
  id: typeof MATCH_SLOT_KEY;
  slotVersion: typeof SLOT_SCHEMA_VERSION;
  kind: "match";
  match: SavedMatchV1;
}>;

type TombstoneSlot = Readonly<{
  id: typeof MATCH_SLOT_KEY;
  slotVersion: typeof SLOT_SCHEMA_VERSION;
  kind: "tombstone";
  revoked: MatchVersion;
  /** Distinct from the revoked live version, so stale writes cannot match it. */
  token: MatchVersion;
}>;

type StoredSlot = MatchSlot | TombstoneSlot;

export type PersistenceClock = Readonly<{
  setTimeout(callback: () => void, delayMs: number): unknown;
  clearTimeout(handle: unknown): void;
}>;

export type OpenMatchDatabase = () => Promise<IDBDatabase>;

export type IndexedDbMatchStorageOptions = Readonly<{
  indexedDB?: IDBFactory;
  databaseName?: string;
  openDatabase?: OpenMatchDatabase;
  clock?: PersistenceClock;
  watchdogMs?: number;
  createBroadcastChannel?: (name: string) => BroadcastChannel;
}>;

export class MatchDatabaseOpenError extends Error {
  readonly code: "open-blocked" | "open-failed";

  constructor(code: "open-blocked" | "open-failed", message: string) {
    super(message);
    this.name = "MatchDatabaseOpenError";
    this.code = code;
  }
}

const systemClock: PersistenceClock = Object.freeze({
  setTimeout(callback, delayMs) {
    return globalThis.setTimeout(callback, delayMs);
  },
  clearTimeout(handle) {
    globalThis.clearTimeout(handle as ReturnType<typeof globalThis.setTimeout>);
  },
});

export function createIndexedDbMatchStorage(
  options: IndexedDbMatchStorageOptions = {},
): StoragePort & MatchHistoryPort {
  const clock = options.clock ?? systemClock;
  const watchdogMs = options.watchdogMs ?? MATCH_COMMIT_WATCHDOG_MS;
  if (!Number.isFinite(watchdogMs) || watchdogMs < 0) {
    throw new RangeError("The match commit watchdog must be a non-negative finite duration.");
  }

  const openDatabase = resolveDatabaseOpener(options);
  let writerTail: Promise<void> = Promise.resolve();
  const listeners = new Set<() => void>();
  const channel = createSyncChannel(options);
  if (channel !== null) {
    channel.onmessage = (event) => {
      if (!isSyncSignal(event.data)) return;
      for (const listener of listeners) listener();
    };
  }

  return Object.freeze({
    loadMatch(): Promise<LoadMatchResult> {
      return writerTail.then(
        () => loadMatch(openDatabase),
        () => loadMatch(openDatabase),
      );
    },
    list() {
      return writerTail.then(
        () => loadHistory(openDatabase, "summaries"),
        () => loadHistory(openDatabase, "summaries"),
      );
    },
    load(id: string) {
      return writerTail.then(
        () => loadHistoryRecord(openDatabase, id),
        () => loadHistoryRecord(openDatabase, id),
      );
    },
    loadAll() {
      return writerTail.then(
        () => loadHistory(openDatabase, "records"),
        () => loadHistory(openDatabase, "records"),
      );
    },
    delete(id: string) {
      return enqueueHistoryMutation(() => deleteHistoryRecord(openDatabase, id));
    },
    clear() {
      return enqueueHistoryMutation(() => clearHistory(openDatabase));
    },
    merge(records: readonly MatchRecordV1[]) {
      return enqueueHistoryMutation(() => mergeHistory(openDatabase, records));
    },

    commitMatch(write: MatchWrite): Promise<CommitResult> {
      const normalized = normalizeWrite(write);
      if (!normalized.ok) return Promise.resolve(unavailable(normalized.reason));

      const operation = writerTail
        .then(
          () => commitMatch(openDatabase, clock, watchdogMs, normalized.write),
          () => commitMatch(openDatabase, clock, watchdogMs, normalized.write),
        )
        .then((result) => {
          if (result.kind === "committed") {
            try {
              channel?.postMessage({ protocol: MATCH_SYNC_CHANNEL });
            } catch {
              // IndexedDB is authoritative; live follower refresh is best effort.
            }
          }
          return result;
        });
      writerTail = operation.then(
        () => undefined,
        () => undefined,
      );
      return operation;
    },
    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    dispose(): void {
      listeners.clear();
      if (channel !== null) {
        channel.onmessage = null;
        channel.close();
      }
    },
  });

  function enqueueHistoryMutation<T>(operation: () => Promise<T>): Promise<T> {
    const pending = writerTail.then(operation, operation).then((result) => {
      try {
        channel?.postMessage({ protocol: MATCH_SYNC_CHANNEL, kind: "history" });
      } catch {
        // IndexedDB remains authoritative; live refresh is best effort.
      }
      return result;
    });
    writerTail = pending.then(
      () => undefined,
      () => undefined,
    );
    return pending;
  }
}

function createSyncChannel(options: IndexedDbMatchStorageOptions): BroadcastChannel | null {
  const factory =
    options.createBroadcastChannel ??
    (typeof window !== "undefined" && typeof BroadcastChannel !== "undefined"
      ? (name: string) => new BroadcastChannel(name)
      : undefined);
  if (factory === undefined) return null;
  try {
    return factory(MATCH_SYNC_CHANNEL);
  } catch {
    return null;
  }
}

function isSyncSignal(value: unknown): boolean {
  return (
    typeof value === "object" &&
    value !== null &&
    Reflect.get(value, "protocol") === MATCH_SYNC_CHANNEL
  );
}

export function openIndexedDbMatchDatabase(
  factory: IDBFactory,
  databaseName = MATCH_DATABASE_NAME,
): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let settled = false;
    let request: IDBOpenDBRequest;
    try {
      request = factory.open(databaseName, MATCH_DATABASE_VERSION);
    } catch (error) {
      reject(new MatchDatabaseOpenError("open-failed", errorMessage(error, "Open failed.")));
      return;
    }

    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(MATCH_OBJECT_STORE)) {
        request.result.createObjectStore(MATCH_OBJECT_STORE, { keyPath: "id" });
      }
      if (!request.result.objectStoreNames.contains(MATCH_HISTORY_OBJECT_STORE)) {
        const history = request.result.createObjectStore(MATCH_HISTORY_OBJECT_STORE, {
          keyPath: "id",
        });
        history.createIndex(MATCH_HISTORY_COMPLETED_INDEX, "completedAt");
      }
    };
    request.onblocked = () => {
      if (settled) return;
      settled = true;
      reject(new MatchDatabaseOpenError("open-blocked", "Match storage opening was blocked."));
    };
    request.onerror = () => {
      if (settled) return;
      settled = true;
      reject(
        new MatchDatabaseOpenError(
          "open-failed",
          errorMessage(request.error, "Match storage could not be opened."),
        ),
      );
    };
    request.onsuccess = () => {
      if (settled) {
        request.result.close();
        return;
      }
      settled = true;
      resolve(request.result);
    };
  });
}

function resolveDatabaseOpener(options: IndexedDbMatchStorageOptions): OpenMatchDatabase | null {
  if (options.openDatabase !== undefined) return options.openDatabase;
  const globalFactory: unknown = Reflect.get(globalThis, "indexedDB");
  const factory = options.indexedDB ?? (globalFactory as IDBFactory | undefined);
  if (factory === undefined) return null;
  const databaseName = options.databaseName ?? MATCH_DATABASE_NAME;
  if (databaseName.length === 0) throw new TypeError("Match database name must not be empty.");
  return () => openIndexedDbMatchDatabase(factory, databaseName);
}

async function loadMatch(openDatabase: OpenMatchDatabase | null): Promise<LoadMatchResult> {
  if (openDatabase === null) {
    return Object.freeze({
      kind: "unavailable",
      reason: failure("not-supported", "IndexedDB is not available."),
    });
  }

  let database: IDBDatabase;
  try {
    database = await openDatabase();
  } catch (error) {
    return Object.freeze({ kind: "unavailable", reason: failureFromOpenError(error) });
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (result: LoadMatchResult) => {
      if (settled) return;
      settled = true;
      database.close();
      resolve(result);
    };

    let transaction: IDBTransaction;
    let request: IDBRequest<unknown>;
    try {
      transaction = database.transaction(MATCH_OBJECT_STORE, "readonly");
      request = transaction.objectStore(MATCH_OBJECT_STORE).get(MATCH_SLOT_KEY);
    } catch (error) {
      finish(
        Object.freeze({
          kind: "unavailable",
          reason: failureFromError(error, "read-failed", "Match storage could not be read."),
        }),
      );
      return;
    }

    request.onerror = () => {
      finish(
        Object.freeze({
          kind: "unavailable",
          reason: failureFromError(
            request.error,
            "read-failed",
            "Match storage could not be read.",
          ),
        }),
      );
    };
    request.onsuccess = () => {
      const parsed = parseStoredSlot(request.result);
      if (!parsed.ok) {
        finish(Object.freeze({ kind: "invalid", error: parsed.error }));
      } else if (parsed.slot === null || parsed.slot.kind === "tombstone") {
        finish(
          Object.freeze({
            kind: "loaded",
            match: null,
            expected: parsed.slot?.token ?? null,
          }),
        );
      } else {
        finish(
          Object.freeze({
            kind: "loaded",
            match: parsed.slot.match,
            expected: versionOf(parsed.slot.match),
          }),
        );
      }
    };
    transaction.onabort = () => {
      finish(
        Object.freeze({
          kind: "unavailable",
          reason: failureFromError(
            transaction.error,
            "transaction-aborted",
            "Match read transaction was aborted.",
          ),
        }),
      );
    };
  });
}

function commitMatch(
  openDatabase: OpenMatchDatabase | null,
  clock: PersistenceClock,
  watchdogMs: number,
  write: MatchWrite,
): Promise<CommitResult> {
  if (openDatabase === null) {
    return Promise.resolve(unavailable(failure("not-supported", "IndexedDB is not available.")));
  }

  return new Promise((resolve) => {
    let settled = false;
    let database: IDBDatabase | null = null;
    let transaction: IDBTransaction | null = null;
    let watchdogHandle: unknown = null;
    let graceHandle: unknown;
    let timedOut = false;
    let plannedResult: CommitResult | null = null;
    let transactionFailure: StorageFailure | null = null;
    const attempted = attemptedVersion(write);

    const finish = (result: CommitResult) => {
      if (settled) return;
      settled = true;
      if (watchdogHandle !== null) clock.clearTimeout(watchdogHandle);
      if (graceHandle !== undefined) clock.clearTimeout(graceHandle);
      database?.close();
      resolve(result);
    };

    const finishIndeterminateUnlessAnEventWins = () => {
      if (settled || graceHandle !== undefined) return;
      graceHandle = clock.setTimeout(() => {
        finish(
          Object.freeze({
            kind: "indeterminate",
            attempted,
            reason: failure(
              "watchdog-expired",
              "Match commit outcome could not be proven after the watchdog expired.",
            ),
          }),
        );
      }, 0);
    };

    const expire = () => {
      if (settled) return;
      timedOut = true;
      if (transaction === null) {
        finish(
          unavailable(
            failure("watchdog-expired", "Match storage did not open before the watchdog expired."),
          ),
        );
        return;
      }
      try {
        transaction.abort();
        finish(
          unavailable(
            failure(
              "watchdog-expired",
              "Match write transaction was aborted after the watchdog expired.",
            ),
          ),
        );
        return;
      } catch {
        // Completion may already be queued. Give its event one task to prove the result.
      }
      finishIndeterminateUnlessAnEventWins();
    };

    watchdogHandle = clock.setTimeout(expire, watchdogMs);

    let opening: Promise<IDBDatabase>;
    try {
      opening = openDatabase();
    } catch (error) {
      finish(unavailable(failureFromOpenError(error)));
      return;
    }

    void opening.then(
      (openedDatabase) => {
        if (settled) {
          openedDatabase.close();
          return;
        }
        database = openedDatabase;
        try {
          transaction = database.transaction(
            [MATCH_OBJECT_STORE, MATCH_HISTORY_OBJECT_STORE],
            "readwrite",
          );
        } catch (error) {
          finish(
            unavailable(
              failureFromError(
                error,
                "write-failed",
                "Match write transaction could not be created.",
              ),
            ),
          );
          return;
        }

        const activeTransaction = transaction;
        activeTransaction.oncomplete = () => {
          finish(
            plannedResult ??
              unavailable(
                failure("write-failed", "Match write transaction completed without a result."),
              ),
          );
        };
        activeTransaction.onabort = () => {
          finish(
            unavailable(
              timedOut
                ? failure(
                    "watchdog-expired",
                    "Match write transaction was aborted after the watchdog expired.",
                  )
                : (transactionFailure ??
                    failureFromError(
                      activeTransaction.error,
                      "transaction-aborted",
                      "Match write transaction was aborted.",
                    )),
            ),
          );
        };
        activeTransaction.onerror = () => {
          if (activeTransaction.error !== null) {
            transactionFailure ??= failureFromError(
              activeTransaction.error,
              "write-failed",
              "Match write transaction failed.",
            );
          }
        };

        let getRequest: IDBRequest<unknown>;
        try {
          getRequest = activeTransaction.objectStore(MATCH_OBJECT_STORE).get(MATCH_SLOT_KEY);
        } catch (error) {
          const readFailure = failureFromError(
            error,
            "read-failed",
            "Current match version could not be read.",
          );
          transactionFailure = readFailure;
          abortOrFinishNoWrite(activeTransaction, () => finish(unavailable(readFailure)));
          return;
        }

        getRequest.onerror = () => {
          transactionFailure = failureFromError(
            getRequest.error,
            "read-failed",
            "Current match version could not be read.",
          );
        };
        getRequest.onsuccess = () => {
          if (timedOut) return;
          const parsed = parseStoredSlot(getRequest.result);
          if (!parsed.ok) {
            plannedResult = unavailable(
              failure("corrupt-data", "Stored match data is corrupt or incompatible."),
            );
            return;
          }

          const decision = decideWrite(parsed.slot, write);
          if (decision.kind === "conflict") {
            plannedResult = Object.freeze({ kind: "conflict", current: decision.current });
            return;
          }

          plannedResult = Object.freeze({ kind: "committed", version: attempted });
          try {
            activeTransaction.objectStore(MATCH_OBJECT_STORE).put(decision.slot);
            const archived = recordForWrite(write);
            if (archived !== null) {
              activeTransaction.objectStore(MATCH_HISTORY_OBJECT_STORE).put(archived);
            }
          } catch (error) {
            const writeFailure = failureFromError(
              error,
              "write-failed",
              "Match data could not be written.",
            );
            transactionFailure = writeFailure;
            plannedResult = unavailable(writeFailure);
            abortOrFinishNoWrite(activeTransaction, () => finish(unavailable(writeFailure)));
          }
        };
      },
      (error: unknown) => {
        finish(unavailable(failureFromOpenError(error)));
      },
    );
  });
}

function recordForWrite(write: MatchWrite): MatchRecordV1 | null {
  if (write.kind === "tombstone") return write.archive ?? null;
  if (write.next.controller.route !== "finished") return null;
  const recording = write.next.recording;
  if (recording?.completedAt === undefined || recording.completedAt === null) return null;
  return createMatchRecord({
    recording,
    mode: write.next.mode,
    humanPlayer: write.next.humanPlayer,
    difficulty: write.next.bot.difficulty,
    difficultyPolicyVersion: write.next.bot.difficultyPolicyVersion,
    result: write.next.controller.result,
  });
}

function loadHistory(
  openDatabase: OpenMatchDatabase | null,
  kind: "records",
): Promise<import("../../features/match/persistence").HistoryLoadResult<readonly MatchRecordV1[]>>;
function loadHistory(
  openDatabase: OpenMatchDatabase | null,
  kind: "summaries",
): Promise<
  import("../../features/match/persistence").HistoryLoadResult<
    readonly import("../../features/match/persistence").MatchHistorySummary[]
  >
>;
async function loadHistory(openDatabase: OpenMatchDatabase | null, kind: "records" | "summaries") {
  const records = await readAllHistory(openDatabase);
  if (records.kind === "unavailable") return records;
  return Object.freeze({
    kind: "loaded" as const,
    value:
      kind === "records" ? records.value : Object.freeze(records.value.map(summarizeMatchRecord)),
  });
}

async function loadHistoryRecord(openDatabase: OpenMatchDatabase | null, id: string) {
  if (openDatabase === null)
    return historyUnavailable("not-supported", "IndexedDB is not available.");
  let database: IDBDatabase;
  try {
    database = await openDatabase();
  } catch (error) {
    return Object.freeze({ kind: "unavailable" as const, reason: failureFromOpenError(error) });
  }
  return new Promise<
    import("../../features/match/persistence").HistoryLoadResult<MatchRecordV1 | null>
  >((resolve) => {
    let transaction: IDBTransaction;
    try {
      transaction = database.transaction(MATCH_HISTORY_OBJECT_STORE, "readonly");
    } catch (error) {
      database.close();
      resolve(
        Object.freeze({
          kind: "unavailable",
          reason: failureFromError(error, "read-failed", "Match history could not be read."),
        }),
      );
      return;
    }
    const request = transaction.objectStore(MATCH_HISTORY_OBJECT_STORE).get(id);
    request.onsuccess = () => {
      database.close();
      if (request.result === undefined) {
        resolve(Object.freeze({ kind: "loaded", value: null }));
        return;
      }
      const parsed = parseMatchRecord(request.result);
      resolve(
        parsed.ok
          ? Object.freeze({ kind: "loaded", value: parsed.record })
          : historyUnavailable("corrupt-data", parsed.message),
      );
    };
    request.onerror = () => {
      database.close();
      resolve(historyUnavailable("read-failed", "Match history could not be read."));
    };
  });
}

async function readAllHistory(
  openDatabase: OpenMatchDatabase | null,
): Promise<import("../../features/match/persistence").HistoryLoadResult<readonly MatchRecordV1[]>> {
  if (openDatabase === null)
    return historyUnavailable("not-supported", "IndexedDB is not available.");
  let database: IDBDatabase;
  try {
    database = await openDatabase();
  } catch (error) {
    return Object.freeze({ kind: "unavailable", reason: failureFromOpenError(error) });
  }
  return new Promise((resolve) => {
    let transaction: IDBTransaction;
    try {
      transaction = database.transaction(MATCH_HISTORY_OBJECT_STORE, "readonly");
    } catch (error) {
      database.close();
      resolve(
        Object.freeze({
          kind: "unavailable",
          reason: failureFromError(error, "read-failed", "Match history could not be read."),
        }),
      );
      return;
    }
    const request = transaction.objectStore(MATCH_HISTORY_OBJECT_STORE).getAll();
    request.onsuccess = () => {
      const parsed: MatchRecordV1[] = [];
      for (const value of request.result as unknown[]) {
        const record = parseMatchRecord(value);
        if (!record.ok) {
          database.close();
          resolve(historyUnavailable("corrupt-data", record.message));
          return;
        }
        parsed.push(record.record);
      }
      parsed.sort(
        (left, right) => right.completedAt - left.completedAt || left.id.localeCompare(right.id),
      );
      database.close();
      resolve(Object.freeze({ kind: "loaded", value: Object.freeze(parsed) }));
    };
    request.onerror = () => {
      database.close();
      resolve(historyUnavailable("read-failed", "Match history could not be read."));
    };
  });
}

async function deleteHistoryRecord(openDatabase: OpenMatchDatabase | null, id: string) {
  return mutateHistory(openDatabase, (store) => {
    store.delete(id);
  });
}

async function clearHistory(openDatabase: OpenMatchDatabase | null) {
  return mutateHistory(openDatabase, (store) => {
    store.clear();
  });
}

async function mutateHistory(
  openDatabase: OpenMatchDatabase | null,
  mutate: (store: IDBObjectStore) => void,
) {
  if (openDatabase === null)
    return historyUnavailable("not-supported", "IndexedDB is not available.");
  let database: IDBDatabase;
  try {
    database = await openDatabase();
  } catch (error) {
    return Object.freeze({ kind: "unavailable" as const, reason: failureFromOpenError(error) });
  }
  return new Promise<import("../../features/match/persistence").HistoryMutationResult>(
    (resolve) => {
      let transaction: IDBTransaction;
      try {
        transaction = database.transaction(MATCH_HISTORY_OBJECT_STORE, "readwrite");
        mutate(transaction.objectStore(MATCH_HISTORY_OBJECT_STORE));
      } catch (error) {
        database.close();
        resolve(
          Object.freeze({
            kind: "unavailable",
            reason: failureFromError(error, "write-failed", "Match history could not be changed."),
          }),
        );
        return;
      }
      transaction.oncomplete = () => {
        database.close();
        resolve(Object.freeze({ kind: "committed" }));
      };
      transaction.onabort = transaction.onerror = () => {
        database.close();
        resolve(historyUnavailable("transaction-aborted", "Match history change was aborted."));
      };
    },
  );
}

async function mergeHistory(
  openDatabase: OpenMatchDatabase | null,
  records: readonly MatchRecordV1[],
) {
  if (openDatabase === null)
    return historyUnavailable("not-supported", "IndexedDB is not available.");
  let database: IDBDatabase;
  try {
    database = await openDatabase();
  } catch (error) {
    return Object.freeze({ kind: "unavailable" as const, reason: failureFromOpenError(error) });
  }
  return new Promise<import("../../features/match/persistence").HistoryMergeResult>((resolve) => {
    let transaction: IDBTransaction;
    try {
      transaction = database.transaction(MATCH_HISTORY_OBJECT_STORE, "readwrite");
    } catch (error) {
      database.close();
      resolve(
        Object.freeze({
          kind: "unavailable",
          reason: failureFromError(error, "write-failed", "Match history could not be imported."),
        }),
      );
      return;
    }
    const store = transaction.objectStore(MATCH_HISTORY_OBJECT_STORE);
    const read = store.getAll();
    let result: import("../../features/match/persistence").HistoryMergeResult | null = null;
    read.onsuccess = () => {
      const existing = new Map<string, MatchRecordV1>();
      for (const value of read.result as unknown[]) {
        const parsed = parseMatchRecord(value);
        if (!parsed.ok) {
          result = historyUnavailable("corrupt-data", parsed.message);
          transaction.abort();
          return;
        }
        existing.set(parsed.record.id, parsed.record);
      }
      let added = 0;
      let skipped = 0;
      for (const record of records) {
        const current = existing.get(record.id);
        if (current !== undefined) {
          if (canonicalRecordJson(current) !== canonicalRecordJson(record)) {
            result = Object.freeze({ kind: "conflict", id: record.id });
            transaction.abort();
            return;
          }
          skipped += 1;
          continue;
        }
        store.put(record);
        added += 1;
      }
      result = Object.freeze({ kind: "committed", added, skipped });
    };
    transaction.oncomplete = () => {
      database.close();
      resolve(result ?? historyUnavailable("write-failed", "History import did not complete."));
    };
    transaction.onabort = () => {
      database.close();
      resolve(result ?? historyUnavailable("transaction-aborted", "History import was aborted."));
    };
    transaction.onerror = () => {
      result ??= historyUnavailable("write-failed", "History import failed.");
    };
  });
}

function historyUnavailable(code: StorageFailureCode, message: string) {
  return Object.freeze({ kind: "unavailable" as const, reason: failure(code, message) });
}

function abortOrFinishNoWrite(transaction: IDBTransaction, finish: () => void): void {
  try {
    transaction.abort();
  } catch {
    finish();
  }
}

type WriteDecision =
  | Readonly<{ kind: "write"; slot: StoredSlot }>
  | Readonly<{ kind: "conflict"; current: MatchVersion | null }>;

function decideWrite(current: StoredSlot | null, write: MatchWrite): WriteDecision {
  const currentVersion = slotVersion(current);

  if (write.kind === "replace") {
    const matches =
      write.expected === null
        ? current === null
        : currentVersion !== null && sameMatchVersion(currentVersion, write.expected);
    return matches
      ? Object.freeze({ kind: "write", slot: matchSlot(write.next) })
      : Object.freeze({ kind: "conflict", current: currentVersion });
  }

  if (
    current?.kind !== "match" ||
    currentVersion === null ||
    !sameMatchVersion(currentVersion, write.expected)
  ) {
    return Object.freeze({ kind: "conflict", current: currentVersion });
  }
  if (write.kind === "advance") {
    return Object.freeze({ kind: "write", slot: matchSlot(write.next) });
  }
  return Object.freeze({ kind: "write", slot: tombstoneSlot(write.expected) });
}

type NormalizedWriteResult =
  Readonly<{ ok: true; write: MatchWrite }> | Readonly<{ ok: false; reason: StorageFailure }>;

function normalizeWrite(input: unknown): NormalizedWriteResult {
  if (!isRecord(input) || typeof input["kind"] !== "string") {
    return invalidWrite("Match write must be an object.");
  }

  if (input["kind"] === "tombstone") {
    const hasArchive = "archive" in input;
    const archive = hasArchive ? parseMatchRecord(input["archive"]) : null;
    if (
      !hasExactlyKeys(input, hasArchive ? ["kind", "expected", "archive"] : ["kind", "expected"]) ||
      !isMatchVersion(input["expected"]) ||
      input["expected"].revision === Number.MAX_SAFE_INTEGER ||
      (archive !== null && !archive.ok)
    ) {
      return invalidWrite("Tombstone write has an invalid expected version.");
    }
    return Object.freeze({
      ok: true,
      write: Object.freeze({
        kind: "tombstone",
        expected: freezeVersion(input["expected"]),
        ...(archive?.ok === true ? { archive: archive.record } : {}),
      }),
    });
  }

  if (
    (input["kind"] !== "advance" && input["kind"] !== "replace") ||
    !hasExactlyKeys(input, ["kind", "expected", "next"])
  ) {
    return invalidWrite("Match write kind or properties are invalid.");
  }
  const parsedNext = parseSavedMatch(input["next"]);
  if (!parsedNext.ok) return invalidWrite(`Next match is invalid: ${parsedNext.error.message}`);

  if (input["kind"] === "advance") {
    if (!isMatchVersion(input["expected"])) {
      return invalidWrite("Advance write has an invalid expected version.");
    }
    if (
      parsedNext.match.epoch !== input["expected"].epoch ||
      parsedNext.match.revision !== input["expected"].revision + 1 ||
      parsedNext.match.revision > Number.MAX_SAFE_INTEGER
    ) {
      return invalidWrite("Advance write must increment the same epoch by exactly one revision.");
    }
    return Object.freeze({
      ok: true,
      write: Object.freeze({
        kind: "advance",
        expected: freezeVersion(input["expected"]),
        next: parsedNext.match,
      }),
    });
  }

  if (input["expected"] !== null && !isMatchVersion(input["expected"])) {
    return invalidWrite("Replace write has an invalid expected version.");
  }
  if (
    parsedNext.match.revision !== 0 ||
    (input["expected"] !== null && parsedNext.match.epoch === input["expected"].epoch)
  ) {
    return invalidWrite("Replace write must start revision zero in a new epoch.");
  }
  return Object.freeze({
    ok: true,
    write: Object.freeze({
      kind: "replace",
      expected: input["expected"] === null ? null : freezeVersion(input["expected"]),
      next: parsedNext.match,
    }),
  });
}

function attemptedVersion(write: MatchWrite): MatchVersion {
  return write.kind === "tombstone" ? tombstoneToken(write.expected) : versionOf(write.next);
}

function matchSlot(match: SavedMatchV1): MatchSlot {
  return Object.freeze({
    id: MATCH_SLOT_KEY,
    slotVersion: SLOT_SCHEMA_VERSION,
    kind: "match",
    match,
  });
}

function tombstoneSlot(revoked: MatchVersion): TombstoneSlot {
  return Object.freeze({
    id: MATCH_SLOT_KEY,
    slotVersion: SLOT_SCHEMA_VERSION,
    kind: "tombstone",
    revoked: freezeVersion(revoked),
    token: tombstoneToken(revoked),
  });
}

function tombstoneToken(revoked: MatchVersion): MatchVersion {
  return Object.freeze({ epoch: revoked.epoch, revision: revoked.revision + 1 });
}

function slotVersion(slot: StoredSlot | null): MatchVersion | null {
  if (slot === null) return null;
  return slot.kind === "match" ? versionOf(slot.match) : slot.token;
}

type StoredSlotParseResult =
  | Readonly<{ ok: true; slot: StoredSlot | null }>
  | Readonly<{ ok: false; error: SavedMatchParseError }>;

function parseStoredSlot(input: unknown): StoredSlotParseResult {
  if (input === undefined) return Object.freeze({ ok: true, slot: null });
  if (!isRecord(input)) return invalidStoredSlot("Stored match slot must be an object.");
  if (!("slotVersion" in input)) {
    return invalidStoredSlot("Stored match slot version is missing.");
  }
  if (input["slotVersion"] !== SLOT_SCHEMA_VERSION) {
    return Object.freeze({
      ok: false,
      error: Object.freeze({
        code: "unsupported-version",
        message: "Stored match slot version is not supported.",
      }),
    });
  }
  if (input["id"] !== MATCH_SLOT_KEY) {
    return invalidStoredSlot("Stored match slot key is invalid.");
  }

  if (
    input["kind"] === "tombstone" &&
    hasExactlyKeys(input, ["id", "slotVersion", "kind", "revoked", "token"]) &&
    isMatchVersion(input["revoked"]) &&
    isMatchVersion(input["token"]) &&
    input["revoked"].revision < Number.MAX_SAFE_INTEGER &&
    sameMatchVersion(input["token"], tombstoneToken(input["revoked"]))
  ) {
    return Object.freeze({ ok: true, slot: tombstoneSlot(input["revoked"]) });
  }
  if (input["kind"] !== "match" || !hasExactlyKeys(input, ["id", "slotVersion", "kind", "match"])) {
    return invalidStoredSlot("Stored match slot is malformed.");
  }

  const parsed = parseSavedMatch(input["match"]);
  if (!parsed.ok) return parsed;
  return Object.freeze({ ok: true, slot: matchSlot(parsed.match) });
}

function invalidStoredSlot(message: string): StoredSlotParseResult {
  return Object.freeze({
    ok: false,
    error: Object.freeze({ code: "malformed", message }),
  });
}

function invalidWrite(message: string): NormalizedWriteResult {
  return Object.freeze({
    ok: false,
    reason: failure("invalid-write", message),
  });
}

function freezeVersion(version: MatchVersion): MatchVersion {
  return Object.freeze({ epoch: version.epoch, revision: version.revision });
}

function unavailable(reason: StorageFailure): CommitResult {
  return Object.freeze({ kind: "unavailable", reason });
}

function failure(code: StorageFailureCode, message: string): StorageFailure {
  return Object.freeze({ code, message });
}

function failureFromOpenError(error: unknown): StorageFailure {
  if (error instanceof MatchDatabaseOpenError) return failure(error.code, error.message);
  return failureFromError(error, "open-failed", "Match storage could not be opened.");
}

function failureFromError(
  error: unknown,
  fallbackCode: StorageFailureCode,
  fallbackMessage: string,
): StorageFailure {
  const name = errorName(error);
  if (name === "QuotaExceededError") {
    return failure("quota-exceeded", "The device denied space for the match save.");
  }
  if (name === "AbortError") {
    return failure("transaction-aborted", "The match storage transaction was aborted.");
  }
  return failure(fallbackCode, fallbackMessage);
}

function errorName(error: unknown): string | null {
  if (typeof error !== "object" || error === null || !("name" in error)) return null;
  return typeof error.name === "string" ? error.name : null;
}

function errorMessage(error: unknown, fallback: string): string {
  if (typeof error !== "object" || error === null || !("message" in error)) return fallback;
  return typeof error.message === "string" && error.message.length > 0 ? error.message : fallback;
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasExactlyKeys(
  record: Readonly<Record<string, unknown>>,
  keys: readonly string[],
): boolean {
  const actual = Object.keys(record).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}
